import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import CustomModal from "@/components/extended/Modal";
import { GlobalModalContextValue, ModalConfig } from "../type/type";

const GlobalModalContext = createContext<GlobalModalContextValue | undefined>(
  undefined,
);

export const GlobalModalProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<ModalConfig | null>(null);
  const hasHandledCloseRef = useRef(false);
  const shouldIgnoreNextCloseRef = useRef(false);

  const openModal = useCallback((nextConfig: ModalConfig) => {
    setConfig(nextConfig);
    setIsOpen(true);
    hasHandledCloseRef.current = false;
    shouldIgnoreNextCloseRef.current = false;
  }, []);

  const closeModal = useCallback(
    (modalKey?: string) => {
      if (modalKey && config?.modalKey !== modalKey) {
        return;
      }

      setIsOpen(false);
      setConfig(null);
      hasHandledCloseRef.current = false;
      shouldIgnoreNextCloseRef.current = false;
    },
    [config?.modalKey],
  );

  const handleCancel = useCallback(() => {
    if (hasHandledCloseRef.current) {
      return;
    }
    hasHandledCloseRef.current = true;
    config?.onCancel?.();
    setIsOpen(false);
    setConfig(null);
    shouldIgnoreNextCloseRef.current = false;
  }, [config]);

  const handleCloseButtonClick = useCallback(() => {
    setIsOpen(false);
    setConfig(null);
    hasHandledCloseRef.current = false;
    shouldIgnoreNextCloseRef.current = false;
  }, []);

  const handleAction = useCallback(async () => {
    const shouldClose = config?.closeOnAction ?? true;
    shouldIgnoreNextCloseRef.current = !shouldClose;

    if (config?.onAction) {
      hasHandledCloseRef.current = true;
      await config.onAction();
    }

    if (shouldClose) {
      setIsOpen(false);
      setConfig(null);
      shouldIgnoreNextCloseRef.current = false;
    }
  }, [config]);

  const value = useMemo(
    () => ({
      openModal,
      closeModal,
    }),
    [openModal, closeModal],
  );

  return (
    <GlobalModalContext.Provider value={value}>
      {children}
      {config && (
        <CustomModal
          open={isOpen}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              const isDismissBlocked =
                config.disableOutsideClose || config.disableEscapeClose;

              if (shouldIgnoreNextCloseRef.current) {
                shouldIgnoreNextCloseRef.current = false;
                return;
              }

              if (isDismissBlocked) {
                return;
              }
            }

            setIsOpen(nextOpen);
            if (!nextOpen) {
              if (!hasHandledCloseRef.current) {
                hasHandledCloseRef.current = true;
                config.onCancel?.();
              }
              setConfig(null);
              shouldIgnoreNextCloseRef.current = false;
            }
          }}
          title={config.title}
          description={config.description}
          descriptionNode={config.descriptionNode}
          descriptionClassName={config.descriptionClassName}
          overlayClassName={config.overlayClassName}
          showCloseButton={config.showCloseButton}
          onCloseButtonClick={
            config.onCloseButtonClick ?? handleCloseButtonClick
          }
          cancelText={config.cancelText}
          actionText={config.actionText}
          onCancel={handleCancel}
          onAction={handleAction}
          className={config.className}
          personName={config.personName}
          variant={config.variant}
          actionVariant={config.actionVariant}
          disableOutsideClose={config.disableOutsideClose}
          disableEscapeClose={config.disableEscapeClose}
        >
          {config.children}
        </CustomModal>
      )}
    </GlobalModalContext.Provider>
  );
};

export const useGlobalModal = (): GlobalModalContextValue => {
  const ctx = useContext(GlobalModalContext);
  if (!ctx) {
    throw new Error("useGlobalModal must be used inside GlobalModalProvider");
  }
  return ctx;
};
